<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Brand logo -->
      <b-link to="/" class="brand-logo">
        <img src="@/assets/companyicons/logo.png" width="150px" alt="" />
      </b-link>

      <!-- Left Illustration -->
      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img fluid :src="sideImg" alt="Signup" />
        </div>
      </b-col>

      <!-- Signup Form -->
      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            {{ $t("SignupPage.CardTitle") }}
          </b-card-title>
          <b-card-text class="mb-2">
            {{ $t("SignupPage.CardSubtitle") }}
          </b-card-text>

          <b-alert
            v-if="serverErrorList.length"
            show
            variant="danger"
            class="mb-1"
          >
            <div v-for="(err, i) in serverErrorList" :key="i">
              {{ err }}
            </div>
          </b-alert>

          <validation-observer ref="signupForm">
            <b-form class="mt-2" @submit.prevent="onSubmit">
              <!-- Contact Email -->
              <b-form-group
                :label="$t('SignupPage.ContactEmailLabel')"
                label-for="contact_email"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="contact_email"
                  :name="$t('SignupPage.ContactEmailLabel')"
                  rules="required|email"
                >
                  <b-form-input
                    id="contact_email"
                    v-model="form.contact_email"
                    :state="errors.length ? false : null"
                    :placeholder="$t('SignupPage.ContactEmailPlaceholder')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Full Name -->
              <b-form-group
                :label="$t('SignupPage.FullNameLabel')"
                label-for="full_name"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="full_name"
                  :name="$t('SignupPage.FullNameLabel')"
                  rules="required"
                >
                  <b-form-input
                    id="full_name"
                    v-model="form.full_name"
                    :state="errors.length ? false : null"
                    :placeholder="$t('SignupPage.FullNamePlaceholder')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Billing Email -->
              <b-form-group
                :label="$t('SignupPage.BillingEmailLabel')"
                label-for="billing_email"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="billing_email"
                  :name="$t('SignupPage.BillingEmailLabel')"
                  rules="required|email"
                >
                  <b-form-input
                    id="billing_email"
                    v-model="form.billing_email"
                    :state="errors.length ? false : null"
                    :placeholder="$t('SignupPage.BillingEmailPlaceholder')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Company Name -->
              <b-form-group
                :label="$t('SignupPage.CompanyNameLabel')"
                label-for="company_name"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="company_name"
                  :name="$t('SignupPage.CompanyNameLabel')"
                  rules="required"
                >
                  <b-form-input
                    id="company_name"
                    v-model="form.company_name"
                    :state="errors.length ? false : null"
                    :placeholder="$t('SignupPage.CompanyNamePlaceholder')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Organisation Number -->
              <b-form-group
                :label="$t('SignupPage.OrganisationNumberLabel')"
                label-for="organisation_number"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="organisation_number"
                  :name="$t('SignupPage.OrganisationNumberLabel')"
                  rules="required"
                >
                  <b-form-input
                    id="organisation_number"
                    v-model="form.organisation_number"
                    :state="errors.length ? false : null"
                    :placeholder="
                      $t('SignupPage.OrganisationNumberPlaceholder')
                    "
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Accounting System -->
              <b-form-group
                :label="$t('SignupPage.AccountingSystemLabel')"
                label-for="accounting_system"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="accounting_system"
                  :name="$t('SignupPage.AccountingSystemLabel')"
                  rules="required"
                >
                  <b-form-select
                    id="accounting_system"
                    v-model="form.accounting_system"
                    :state="errors.length ? false : null"
                    :options="accountingSystemOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Country -->
              <b-form-group
                :label="$t('SignupPage.CountryLabel')"
                label-for="country"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="country"
                  :name="$t('SignupPage.CountryLabel')"
                  rules="required"
                >
                  <b-form-select
                    id="country"
                    v-model="form.country"
                    :state="errors.length ? false : null"
                    :options="countryOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Address -->
              <b-form-group
                :label="$t('SignupPage.AddressLabel')"
                label-for="address"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="address"
                  :name="$t('SignupPage.AddressLabel')"
                  rules="required"
                >
                  <b-form-input
                    id="address"
                    v-model="form.address"
                    :state="errors.length ? false : null"
                    :placeholder="$t('SignupPage.AddressPlaceholder')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- City & Zip -->
              <b-row>
                <b-col>
                  <b-form-group
                    :label="$t('SignupPage.CityLabel')"
                    label-for="city"
                  >
                    <validation-provider
                      #default="{ errors }"
                      vid="city"
                      :name="$t('SignupPage.CityLabel')"
                      rules="required"
                    >
                      <b-form-input
                        id="city"
                        v-model="form.city"
                        :state="errors.length ? false : null"
                        :placeholder="$t('SignupPage.CityPlaceholder')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    :label="$t('SignupPage.ZipCodeLabel')"
                    label-for="zip_code"
                  >
                    <validation-provider
                      #default="{ errors }"
                      vid="zip_code"
                      :name="$t('SignupPage.ZipCodeLabel')"
                      rules="required"
                    >
                      <b-form-input
                        id="zip_code"
                        v-model="form.zip_code"
                        :state="errors.length ? false : null"
                        :placeholder="$t('SignupPage.ZipCodePlaceholder')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Optional: Phone -->
              <b-form-group
                :label="$t('SignupPage.PhoneLabel')"
                label-for="phone"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="phone"
                  :name="$t('SignupPage.PhoneLabel')"
                >
                  <b-form-input
                    id="phone"
                    v-model="form.phone"
                    :state="errors.length ? false : null"
                    :placeholder="$t('SignupPage.PhonePlaceholder')"
                  />
                </validation-provider>
              </b-form-group>

              <!-- Optional: Onboarding Care -->
              <b-form-group>
                <b-form-checkbox
                  v-model="form.onboarding_care"
                  class="custom-control-primary"
                >
                  {{ $t("SignupPage.OnboardingCare") }}
                  <feather-icon
                    v-b-tooltip.hover.v-primary
                    size="16"
                    :title="$t('SignupPage.OnboardingCareTooltip')"
                    icon="HelpCircleIcon"
                    class="ml-50"
                  />
                </b-form-checkbox>
              </b-form-group>

              <!-- Plan: Package -->
              <b-form-group
                :label="$t('SignupPage.PackageLabel')"
                label-for="package"
              >
                <validation-provider
                  #default="{ errors }"
                  vid="package"
                  :name="$t('SignupPage.PackageLabel')"
                  rules="required"
                >
                  <b-form-select
                    id="package"
                    v-model.number="form.package"
                    :state="errors.length ? false : null"
                    :options="packageOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Required: Accept Terms of Purchase -->
              <b-form-group>
                <validation-provider
                  #default="{ errors }"
                  vid="accept_terms"
                  :name="$t('SignupPage.TermsOfPurchaseLink')"
                  rules="required"
                >
                  <b-form-checkbox
                    v-model="form.accept_terms"
                    :state="errors.length ? false : null"
                    class="custom-control-primary"
                  >
                    {{ $t("SignupPage.AcceptTermsPrefix") }}
                    <a
                      :href="buildTermsUrl()"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click.stop
                      @mousedown.stop
                      class="ml-25"
                    >
                      {{ $t("SignupPage.TermsOfPurchaseLink") }}
                    </a>
                  </b-form-checkbox>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Submit -->
              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="submitting || !form.accept_terms"
              >
                {{
                  submitting
                    ? $t("SignupPage.Submitting")
                    : $t("SignupPage.Submit")
                }}
              </b-button>

              <b-card-text class="text-center mt-2">
                <b-link :to="{ name: 'login' }"
                  ><small>{{ $t("SignupPage.BackToLogin") }}</small></b-link
                >
              </b-card-text>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
      <!-- /Signup Form -->
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import http from "@axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "Signup",
  components: { ValidationProvider, ValidationObserver, ToastificationContent },
  data() {
    return {
      sideImg: require("@/assets/images/pages/register-v2.svg"),
      required,
      email,
      submitting: false,
      serverErrorList: [],
      form: {
        contact_email: "",
        full_name: "",
        billing_email: "",
        company_name: "",
        organisation_number: "",
        accounting_system: null,
        country: "SE",
        address: "",
        city: "",
        zip_code: "",
        phone: "",
        onboarding_care: false,
        accept_terms: false,
        package: null,
      },
      packageOptions: [],
      countryOptions: [
        { value: "SE", text: "Sverige" },
        { value: "NO", text: "Norge" },
        { value: "DK", text: "Danmark" },
        { value: "FI", text: "Finland" },
      ],
    };
  },
  computed: {
    accountingSystemOptions() {
      return [
        {
          value: null,
          text: this.$t("SignupPage.AccountingSystemPlaceholder"),
          disabled: true,
        },
        {
          value: "fortnox",
          text: this.$t("SignupPage.AccountingSystemFortnox"),
        },
        {
          value: "spiris",
          text: this.$t("SignupPage.AccountingSystemSpiris"),
        },
      ];
    },
  },
  async mounted() {
    // Load options for selects
    this.loadPlans();
    // Preselect package if passed from landing page
    const q =
      this.$route && this.$route.query ? this.$route.query.package : null;
    if (q !== undefined && q !== null) {
      const value = Array.isArray(q) ? q[0] : q;
      const n = typeof value === "string" ? parseInt(value, 10) : value;
      this.form.package = Number.isNaN(n) ? value : n;
    }
    // Preselect accounting system if passed from integrations pages
    const systemQ =
      this.$route && this.$route.query
        ? this.$route.query.accounting_system
        : null;
    if (systemQ !== undefined && systemQ !== null) {
      const value = Array.isArray(systemQ) ? systemQ[0] : systemQ;
      const normalized =
        typeof value === "string" ? value.toLowerCase() : value;
      if (["fortnox", "spiris"].includes(normalized)) {
        this.form.accounting_system = normalized;
      }
    }
  },
  methods: {
    async bootstrapCsrf() {
      try {
        await http.get("auth/csrf");
      } catch (e) {
        /* ignore */
      }
    },
    buildTermsUrl() {
      const proxyTermsPath = "/api/terms-of-purchase";
      const appendTermsPath = (base) =>
        `${String(base).replace(/\/+$/, "")}/terms-of-purchase`;
      const rawBase =
        (http && http.defaults && http.defaults.baseURL) || "/api";
      const base = String(rawBase).replace(/\/+$/, "");
      if (base.startsWith("/")) return appendTermsPath(base);
      if (/^https?:\/\//i.test(base)) {
        try {
          const win = typeof window !== "undefined" ? window : null;
          if (win && win.location) {
            const parsed = new URL(base, win.location.origin);
            if (parsed.origin === win.location.origin) {
              return appendTermsPath(parsed.pathname || "/api");
            }
          }
        } catch (e) {
          // ignore parse errors and use proxy path fallback
        }
      }
      return proxyTermsPath;
    },
    async loadPlans() {
      try {
        // Load public plan info like on the landing page
        const res = await http.get("plan-info/", {
          transformRequest: (data, headers) => {
            delete headers.Authorization;
            return data;
          },
        });
        const plans = (res.data && res.data.data) || [];
        this.packageOptions = plans.map((p, i) => {
          // Always prefer the backend package id; fall back only if truly missing
          const pkgId =
            p.id ||
            (p.package && p.package.id) ||
            p.package_id ||
            p.plan_id ||
            p.pk;
          return {
            value: pkgId != null ? pkgId : p.level != null ? p.level : i,
            text: p.plan_name,
          };
        });
      } catch (e) {
        // Non-blocking; user can still fill IDs manually
      }
    },
    async onSubmit() {
      const ok = await this.$refs.signupForm.validate();
      if (!ok) return;
      this.submitting = true;
      try {
        await this.bootstrapCsrf();
        const payload = {
          ...this.form,
          accounting_program: this.form.accounting_system,
        };
        delete payload.accounting_system;
        await http.post("auth/signup", payload);

        // Success toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("SignupPage.SuccessTitle"),
            icon: "CheckIcon",
            variant: "success",
            text: this.$t("SignupPage.SuccessText"),
          },
        });
        // Redirect to login
        this.$router.push({ name: "login" });
      } catch (error) {
        if (error.response && error.response.status === 400) {
          const serverErrors =
            (error.response.data && error.response.data.error) || {};
          const knownVids = [
            "contact_email",
            "full_name",
            "billing_email",
            "company_name",
            "organisation_number",
            "accounting_system",
            "accounting_program",
            "country",
            "address",
            "city",
            "zip_code",
            "phone",
            "package",
          ];
          const backendToFieldVid = {
            accounting_program: "accounting_system",
          };
          const mapped = {};
          const extra = [];
          if (serverErrors && typeof serverErrors === "object") {
            Object.keys(serverErrors).forEach((k) => {
              const msgs = Array.isArray(serverErrors[k])
                ? serverErrors[k]
                : [String(serverErrors[k])];
              const targetVid = backendToFieldVid[k] || k;
              if (knownVids.includes(k)) {
                mapped[targetVid] = msgs;
              } else {
                extra.push(`${k}: ${msgs.join(", ")}`);
              }
            });
            if (Object.keys(mapped).length)
              this.$refs.signupForm.setErrors(mapped);
            this.serverErrorList = extra;
          } else {
            this.serverErrorList = [
              this.$t("SignupPage.ValidationErrorFallback"),
            ];
          }
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("SignupPage.ValidationErrorTitle"),
              icon: "AlertTriangleIcon",
              variant: "danger",
              text:
                this.serverErrorList[0] ||
                this.$t("SignupPage.ValidationErrorText"),
            },
          });
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("SignupPage.FailedTitle"),
              icon: "AlertTriangleIcon",
              variant: "danger",
              text: this.$t("SignupPage.FailedText"),
            },
          });
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
